import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../i18n/LanguageContext';
import { SkeletonList } from '../components/Skeleton';
import RideDetailsModal from '../components/RideDetailsModal';
import RatingSheet from '../components/RatingSheet';
import SupportModal from '../components/SupportModal';
import { getOlderUserRides, submitRideRating, getUserRideStats } from '../services/firebase';
import { hapticSelection } from '../utils/haptics';
import { toMillis } from '../utils/rideData';
import {
  mergeRideSources, withIntertownTrips, withRating, hasMoreAfterRefresh, filterEmptyKey, needsYear,
  saveHistorySession, rememberHistoryScroll, readHistorySession, clearHistoryScroll,
} from '../utils/rideHistory';
import { formatRupees } from '../utils/intertownDisplay';

const ACTIVE = ['searching', 'driver_assigned', 'arrived', 'started'];

// Must match RIDE_STATS_VERSION in firebase/functions/index.js. When the server
// bumps it, the profile counters are considered stale until getUserRideStats
// re-scans them with the current distance formula (booked route distance, not
// the raw GPS trail).
const RIDE_STATS_VERSION = 2;

// Keep the last authoritative totals available while the Rides tab remounts
// or refreshes. Values are scoped by Firebase user so they cannot cross users.
const lifetimeStatsCache = new Map();
const STATS_CACHE_KEY = 'mtl_rides_lifetime_stats_v2';

// localStorage copy so the header shows real numbers on the very first frame
// after a cold start, before the profile snapshot / backfill call return.
const loadStatsCache = (uid) => {
  if (!uid) return null;
  try {
    const raw = JSON.parse(localStorage.getItem(STATS_CACHE_KEY) || 'null');
    if (raw?.uid !== uid || !Number.isFinite(Number(raw.completedCount))) return null;
    return { completedCount: Number(raw.completedCount), distanceKm: Number(raw.distanceKm) || 0 };
  } catch {
    return null;
  }
};
const saveStatsCache = (uid, stats) => {
  if (!uid || !stats || !Number.isFinite(Number(stats.completedCount))) return;
  try {
    localStorage.setItem(STATS_CACHE_KEY, JSON.stringify({ uid, ...stats }));
  } catch { /* storage full / private mode */ }
};

const AutoRickshawIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 100 100"
    fill="none"
    stroke="currentColor"
    strokeWidth="7"
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M88 15H18C10 15 4 21 4 29V66C4 70 7 73 11 73H13" />
    <path d="M29 73H72" />
    <path d="M34 18V36H6" />
    <path d="M6 46H34" />
    <path d="M38 18V46C38 52 42 56 48 56H64V18" />
    <path d="M68 18V44H84L78 18H68Z" />
    <path d="M78 18L86 48V58H68" />
    <path d="M86 58L97 68H78L86 58Z" />
    <circle cx="21" cy="76" r="9" strokeWidth="7" />
    <circle cx="87" cy="78" r="9" strokeWidth="7" />
  </svg>
);

const LeafIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/>
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
  </svg>
);

const RouteDistanceIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="6" cy="19" r="3"/>
    <path d="M9 19h8.5a4.5 4.5 0 0 0 0-9H7a4 4 0 0 1 0-8h11"/>
    <polyline points="15 5 18 2 21 5"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const StarRating = ({ rating = 5, label }) => (
  <div className="ride-stars-row" aria-label={label || `Rating: ${rating} stars`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <svg key={s} width="13" height="13" viewBox="0 0 24 24" fill={s <= rating ? '#00A86B' : '#E5E7EB'} aria-hidden="true">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ))}
  </div>
);

export default function RideHistory() {
  const { user, userProfile, rides, intertownRequests, ridesLoaded, ridesHasMore, profileReady } = useApp();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  // What this tab remembered while the app stayed open (same rider only): the filter and the
  // pages already loaded, so opening a receipt and coming back does not start over.
  const [remembered] = useState(() => readHistorySession(user?.uid));
  const [filter, setFilter] = useState(remembered?.filter || 'all'); // all | completed | cancelled
  const [selectedRide, setSelectedRide] = useState(null);
  const [showSupport, setShowSupport] = useState(false);
  const [ratingRide, setRatingRide] = useState(null);
  const [ratingBusy, setRatingBusy] = useState(false);
  const ratingInFlight = useRef(false);
  const [olderRides, setOlderRides] = useState(remembered?.olderRides || []);
  const olderCountRef = useRef(0);
  olderCountRef.current = olderRides.length;
  // Read inside the first-page fetch without making it refetch whenever this changes.
  const ridesHasMoreRef = useRef(ridesHasMore);
  ridesHasMoreRef.current = ridesHasMore;
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(remembered?.hasMore || false);
  const [lifetimeStats, setLifetimeStats] = useState(() => (
    user?.uid ? (lifetimeStatsCache.get(user.uid) || loadStatsCache(user.uid)) : null
  ));

  const getTrans = (key, fallback) => {
    const val = t(key);
    if (!val || val === key || val.toUpperCase() === key.toUpperCase()) {
      return fallback;
    }
    return val;
  };

  const handleRatingSubmit = async (feedback) => {
    // A ref, not the busy state: two quick taps must not both get through.
    if (!ratingRide?.id || ratingInFlight.current) return;
    ratingInFlight.current = true;
    const ratedId = ratingRide.id;
    setRatingBusy(true);
    try {
      await submitRideRating(ratedId, feedback);
      // Only the newest rides are live. The older pages are a one-off snapshot, so show
      // the new rating on that card now (it would keep saying "Rate ride" and a second
      // tap would be refused: a ride can only be rated once).
      setOlderRides((prev) => withRating(prev, ratedId, feedback.rating));
      toast.success(getTrans('rating.thankYou', 'Thank you for your rating!'));
      setRatingRide(null);
    } catch {
      toast.error(getTrans('authErrors.default', 'Failed to submit rating'));
    } finally {
      ratingInFlight.current = false;
      setRatingBusy(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    let d = null;
    if (ts?.toDate && typeof ts.toDate === 'function') d = ts.toDate();
    else if (ts instanceof Date) d = ts;
    else if (typeof ts === 'number') d = new Date(ts);
    else if (ts?.seconds) d = new Date(ts.seconds * 1000);
    else if (typeof ts === 'string') d = new Date(ts);
    if (!d || Number.isNaN(d.getTime())) return '';

    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const locale = lang === 'te' ? 'te-IN' : 'en-IN';
    const timeStr = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: true });
    if (isToday) return `${t('history.today')}, ${timeStr}`;
    if (isYesterday) return `${t('history.yesterday')}, ${timeStr}`;

    return d.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      // A ride from an earlier year must say so, or it reads like this year's.
      ...(needsYear(d, now) ? { year: 'numeric' } : {}),
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Local rides only: these are the pages, and "Load older rides" pages on from the last one.
  const allRides = useMemo(() => mergeRideSources(olderRides, rides || []), [olderRides, rides]);
  const completedRides = useMemo(() => allRides.filter((r) => r.status === 'completed'), [allRides]);

  // AppContext only streams a handful of the newest rides now (startup cost).
  // The Rides tab owns its own history: fetch a full first page when it opens.
  // `olderRides` then holds the paginated list; context `rides` overlays the
  // live active trip on top via the dedup merge in `allRides`.
  const [firstPageLoading, setFirstPageLoading] = useState(!remembered);
  // What the list shows: local rides plus finished Intertown trips (their own collection).
  // Until the first page of older rides is in, more local rides may exist: same as `hasMore`.
  const listRides = useMemo(
    () => withIntertownTrips(allRides, intertownRequests, { hasMore: hasMore || firstPageLoading }),
    [allRides, intertownRequests, hasMore, firstPageLoading],
  );
  const filtered = useMemo(() => {
    if (filter === 'active') return listRides.filter((r) => ACTIVE.includes(r.status));
    if (filter === 'completed') return listRides.filter((r) => r.status === 'completed');
    if (filter === 'cancelled') return listRides.filter((r) => r.status === 'cancelled');
    return listRides;
  }, [listRides, filter]);
  // Only the first run after the tab opens may reuse what was remembered; another rider never does.
  const reuseRemembered = useRef(Boolean(remembered));
  const firstPageDone = useRef(Boolean(remembered));
  useEffect(() => {
    const reuse = reuseRemembered.current;
    reuseRemembered.current = false;
    if (!reuse) {
      setOlderRides([]);
      setHasMore(false);
      firstPageDone.current = false;
    }
    setLifetimeStats(user?.uid ? (lifetimeStatsCache.get(user.uid) || loadStatsCache(user.uid)) : null);
    if (!user?.uid) { setFirstPageLoading(false); return undefined; }
    let cancelled = false;
    // With remembered rides on screen already, refresh quietly instead of showing the skeleton.
    if (!reuse) setFirstPageLoading(true);
    getOlderUserRides(user.uid, Date.now(), 40)
      .then((page) => {
        if (cancelled) return;
        if (page.error) {
          // getOlderUserRides reports a failure instead of throwing. The newest rides still
          // show from the live list; if the rider has more, keep "Load older rides" so they can
          // retry rather than silently hiding them.
          if (!reuse && ridesHasMoreRef.current) setHasMore(true);
          return;
        }
        const fresh = page.rides || [];
        setOlderRides((prev) => (reuse ? mergeRideSources(prev, fresh) : fresh));
        setHasMore((prev) => hasMoreAfterRefresh({
          cachedCount: reuse ? olderCountRef.current : 0,
          freshCount: fresh.length,
          cachedHasMore: prev,
          freshHasMore: page.hasMore,
        }));
        firstPageDone.current = true;
      })
      .catch(() => { /* context rides still cover the recent list */ })
      .finally(() => { if (!cancelled) setFirstPageLoading(false); });
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Remember the list while the app stays open (see utils/rideHistory.js).
  useEffect(() => {
    if (user?.uid && firstPageDone.current) saveHistorySession(user.uid, { filter, olderRides, hasMore });
  }, [user?.uid, filter, olderRides, hasMore]);

  // Coming back from a receipt: put the list back where it was, once the cards are on screen.
  const showSkeleton = !profileReady || !ridesLoaded || (firstPageLoading && allRides.length === 0);
  const scrollToRestore = useRef(remembered?.scrollY ?? null);
  useLayoutEffect(() => {
    if (scrollToRestore.current == null || showSkeleton || filtered.length === 0) return;
    window.scrollTo(0, scrollToRestore.current);
    scrollToRestore.current = null;
    clearHistoryScroll(user?.uid);
  }, [showSkeleton, filtered.length, user?.uid]);


  const liveCompletedCount = useMemo(
    () => (rides || []).filter((r) => r.status === 'completed').length,
    [rides],
  );

  // Primary source: running totals on the profile — already loaded by AppContext
  // (real-time, cached), so they paint fast on every open with no extra call.
  const profileCompleted = Number(userProfile?.totalCompletedRides);
  const profileDistanceKm = Number(userProfile?.totalDistanceKm);
  // Trust the profile counters only once the server has (re)computed them with
  // the current distance formula. A version mismatch → treat as absent so the
  // one-time getUserRideStats re-scan below runs and corrects the totals.
  const profileStatsFresh = Number(userProfile?.rideStatsVersion) === RIDE_STATS_VERSION;
  const profileStats = (profileStatsFresh && Number.isFinite(profileCompleted))
    ? { completedCount: profileCompleted, distanceKm: Number.isFinite(profileDistanceKm) ? profileDistanceKm : 0 }
    : null;

  // The callable is now only a one-time backfill for riders who predate the
  // profile counters — never called once the profile has them.
  useEffect(() => {
    if (!user?.uid || profileStats) return undefined;
    let cancelled = false;
    getUserRideStats().then((stats) => {
      if (!cancelled && stats) {
        lifetimeStatsCache.set(user.uid, stats);
        saveStatsCache(user.uid, stats);
        setLifetimeStats(stats);
      }
    });
    return () => { cancelled = true; };
  }, [user?.uid, profileStats, liveCompletedCount]);

  useEffect(() => {
    if (user?.uid && profileStats) {
      lifetimeStatsCache.set(user.uid, profileStats);
      saveStatsCache(user.uid, profileStats);
    }
  }, [user?.uid, profileStats?.completedCount, profileStats?.distanceKm]);

  // profile → cached/backfill → (last resort) the loaded list, so the header is
  // never a permanent skeleton even if every server path fails.
  const bestStats = profileStats || lifetimeStats;
  const headerCompletedCount = bestStats?.completedCount
    ?? (completedRides.length > 0 ? completedRides.length : null);
  const headerDistanceKm = bestStats && bestStats.distanceKm > 0
    ? bestStats.distanceKm.toFixed(1)
    : null;

  const loadOlder = async () => {
    if (loadingOlder || !hasMore || !user?.uid || !rides?.length) return;
    const oldest = allRides[allRides.length - 1];
    const beforeMillis = toMillis(oldest?.createdAt);
    if (!Number.isFinite(beforeMillis) || beforeMillis <= 0) {
      setHasMore(false);
      return;
    }
    setLoadingOlder(true);
    try {
      const page = await getOlderUserRides(user.uid, beforeMillis, 40);
      // getOlderUserRides reports a failure instead of throwing. Say so and keep the button,
      // so the rider can try again instead of believing this is the end of their rides.
      if (page.error) {
        toast.error(getTrans('history.loadOlderFailed', 'Could not load older rides'));
        return;
      }
      setOlderRides((prev) => [...prev, ...(page.rides || [])]);
      setHasMore(Boolean(page.hasMore));
    } catch {
      toast.error(getTrans('history.loadOlderFailed', 'Could not load older rides'));
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleFilterClick = (key) => {
    hapticSelection();
    setFilter(key);
  };

  const filterOptions = [
    { key: 'all', label: t('history.filter_all') || 'All' },
    { key: 'completed', label: t('history.filter_completed') || 'Completed' },
    { key: 'cancelled', label: t('history.filter_cancelled') || 'Cancelled' },
  ];
  const activeFilterIndex = Math.max(0, filterOptions.findIndex(({ key }) => key === filter));

  const handleCardClick = (ride) => {
    hapticSelection();
    rememberHistoryScroll(user?.uid, window.scrollY); // restored when the rider comes back
    if (ride.kind === 'intertown') {
      navigate(`/rides/intertown/${ride.requestId}`);
    } else if (ACTIVE.includes(ride.status)) {
      navigate(`/active/${ride.id}`);
    } else {
      navigate(`/rides/${ride.id}`);
    }
  };

  const executeRebook = (ride) => {
    hapticSelection();
    setSelectedRide(null);
    navigate('/', {
      state: {
        rebookDrop: ride.destination?.lat
          ? {
              name: ride.destination.name || ride.destination.address,
              address: ride.destination.address || ride.destination.name,
              lat: ride.destination.lat,
              lng: ride.destination.lng,
            }
          : null,
        rebookPickup: ride.pickup?.lat
          ? {
              name: ride.pickup.name || ride.pickup.address,
              address: ride.pickup.address || ride.pickup.name,
              lat: ride.pickup.lat,
              lng: ride.pickup.lng,
            }
          : null,
      },
    });
  };

  const handleRebookClick = (e, ride) => {
    e.stopPropagation();
    executeRebook(ride);
  };

  return (
    <div className="app rides-page-container">
      {/* 10/10 Premium Header */}
      <header className="rides-header">
        <div className="rides-header-top">
          <div className="rides-header-text">
            <h1 className="rides-title">{t('history.title') || 'My Rides'}</h1>
            <p className="rides-subtitle">{t('history.subtitle') || 'Your ride history & details'}</p>
          </div>
        </div>

        {/* Quick Summary Metrics Cards (Completed Rides & Distance Covered) */}
        {ridesLoaded && allRides.length > 0 && (
          <div className="rides-metrics-row">
            <div className="rides-metric-card">
              <div className="rides-metric-ico auto">
                <AutoRickshawIcon />
              </div>
              <div className="rides-metric-info">
                {headerCompletedCount != null ? (
                  <span className="rides-metric-value">{headerCompletedCount}</span>
                ) : (
                  <span className="rides-metric-value rides-metric-value-skeleton" aria-label={t('common.loading')} />
                )}
                <span className="rides-metric-label">{t('history.completedRides') || 'Completed'}</span>
              </div>
            </div>
            <div className="rides-metric-card">
              <div className="rides-metric-ico dist">
                <RouteDistanceIcon />
              </div>
              <div className="rides-metric-info">
                {headerDistanceKm != null ? (
                  <span className="rides-metric-value">{headerDistanceKm} km</span>
                ) : (
                  <span className="rides-metric-value rides-metric-value-skeleton" aria-label={t('common.loading')} />
                )}
                <span className="rides-metric-label">{t('history.distanceCovered') || 'Distance'}</span>
              </div>
            </div>
          </div>
        )}
      </header>

      <div className="page-content history-page">
        {showSkeleton ? (
          <div style={{ paddingTop: 16 }}>
            <SkeletonList count={4} cardHeight="130px" />
          </div>
        ) : (
          <>
            {/* Segmented Filter Control */}
            {listRides.length > 0 && (
              <div className="history-filters-container">
                <div className="history-filters-pill-bar" role="tablist" aria-label={t('history.title')}>
                  <div
                    className="history-filter-glider"
                    style={{ transform: `translateX(${activeFilterIndex * 100}%)` }}
                    aria-hidden="true"
                  />
                  {filterOptions.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={filter === key}
                      className={`history-filter-pill${filter === key ? ' active' : ''}`}
                      onClick={() => handleFilterClick(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {filtered.length === 0 && !firstPageLoading && listRides.length === 0 ? (
              <div className="history-empty">
                <div className="history-empty-ico" aria-hidden>
                  <AutoRickshawIcon />
                </div>
                <p className="history-empty-title">{t('history.empty')}</p>
                <p className="text-muted">{t('history.emptySub')}</p>
                <button type="button" className="btn btn-primary" onClick={() => navigate('/')}>
                  {t('nav.home')}
                </button>
              </div>
            ) : filtered.length === 0 && !firstPageLoading ? (
              // The rider HAS rides; this tab just has none among those loaded so far.
              <div className="history-empty">
                <div className="history-empty-ico" aria-hidden>
                  <AutoRickshawIcon />
                </div>
                <p className="history-empty-title">{t(filterEmptyKey(filter))}</p>
                {hasMore && <p className="text-muted">{t('history.noMatchMore')}</p>}
                {hasMore && (
                  <button type="button" className="btn btn-outline" onClick={loadOlder} disabled={loadingOlder}>
                    {loadingOlder ? (t('history.loadingOlder') || 'Loading…') : (t('history.loadOlder') || 'Load older rides')}
                  </button>
                )}
                <button type="button" className="btn btn-primary" onClick={() => handleFilterClick('all')}>
                  {t('history.showAll')}
                </button>
              </div>
            ) : (
              <div className="rides-list-stack">
                {filtered.map((ride) => {
                  const isActive = ACTIVE.includes(ride.status);
                  const isCompleted = ride.status === 'completed';
                  const canRebook = !isActive && isCompleted && ride.destination?.lat;
                  const isIntertown = ride.kind === 'intertown';

                  const pickupTitle = ride.pickup?.name || ride.pickup?.address || t('book.pickup');
                  const dropTitle = ride.destination?.name || ride.destination?.address || t('book.drop');

                  return (
                    <div
                      key={ride.id}
                      className={`premium-ride-card${isActive ? ' active-ride' : ''}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleCardClick(ride)}
                      onKeyDown={(e) => {
                        // Only the card itself: Enter/Space on the "Rate ride" or "Book again"
                        // button inside it must not also open the receipt.
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCardClick(ride);
                        }
                      }}
                    >
                      {/* Card Header: Auto Icon + Date + Status + Fare */}
                      <div className="premium-card-head">
                        <div className="card-left-group">
                          <div className={`card-vehicle-badge ${ride.status}`}>
                            <AutoRickshawIcon />
                          </div>
                          <div className="card-meta">
                            <span className="card-date">{formatDate(ride.createdAt)}</span>
                            {/* Nothing is charged for a cancelled ride; the quoted fare would mislead. */}
                            {ride.status !== 'cancelled' && (
                              <strong className="card-fare">{isIntertown ? formatRupees(ride.fare) : `₹${ride.fare || 0}`}</strong>
                            )}
                          </div>
                        </div>

                        <div className="card-right-group">
                          <span className={`status-pill status-${ride.status}`}>
                            {t(`status.${ride.status}`)}
                          </span>
                          <span className="card-chevron" aria-hidden>
                            <ChevronRightIcon />
                          </span>
                        </div>
                      </div>

                      {/* Vertical Route Timeline */}
                      <div className="ride-route-timeline">
                        <div className="timeline-track" aria-hidden>
                          <span className="timeline-dot pickup" />
                          <span className="timeline-line" />
                          <span className="timeline-dot drop" />
                        </div>
                        <div className="timeline-labels">
                          <div className="timeline-row">
                            <span className="timeline-addr pickup">{pickupTitle}</span>
                          </div>
                          <div className="timeline-row">
                            <span className="timeline-addr drop">{dropTitle}</span>
                          </div>
                        </div>
                      </div>

                      {/* Card Action Footer */}
                      {isIntertown && (
                        <div className="premium-card-footer">
                          <span className="intertown-tag">
                            {t('intertown.pageTitle')} · {t(ride.tripType === 'return' ? 'intertown.returnTrip' : 'intertown.oneWay')}
                          </span>
                        </div>
                      )}
                      {/* Only finished rides have anything to show here; an empty footer is dead space. */}
                      {!isIntertown && isCompleted && (
                      <div className="premium-card-footer">
                        {isCompleted && (
                          ride.rating ? (
                            <StarRating rating={Number(ride.rating)} label={t('details.yourRating', { rating: Number(ride.rating) })} />
                          ) : (
                            <button
                              type="button"
                              className="btn-rate-pill"
                              onClick={(e) => {
                                e.stopPropagation();
                                hapticSelection();
                                setRatingRide(ride);
                              }}
                            >
                              <span className="rate-star-icon">⭐</span>
                              <span>{getTrans('rating.rateTrip', 'Rate ride')}</span>
                            </button>
                          )
                        )}
                        {canRebook && (
                          <button
                            type="button"
                            className="btn-rebook-pill"
                            onClick={(e) => handleRebookClick(e, ride)}
                          >
                            <RefreshIcon />
                            <span>{getTrans('history.rebook', 'Book again')}</span>
                          </button>
                        )}
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {filtered.length > 0 && hasMore && (
              <button type="button" className="btn btn-outline btn-block" onClick={loadOlder} disabled={loadingOlder} style={{ marginTop: 16 }}>
                {loadingOlder ? (t('history.loadingOlder') || 'Loading…') : (t('history.loadOlder') || 'Load older rides')}
              </button>
            )}
          </>
        )}
      </div>

      {/* Rating Sheet for Unrated Rides */}
      {ratingRide && (
        <RatingSheet
          t={t}
          fare={ratingRide.fare}
          busy={ratingBusy}
          onSubmit={handleRatingSubmit}
          onSkip={() => setRatingRide(null)}
        />
      )}

      {/* Ride Details Modal */}
      {selectedRide && (
        <RideDetailsModal
          ride={selectedRide}
          onClose={() => setSelectedRide(null)}
          onRebook={(ride) => executeRebook(ride)}
          onCallSupport={() => {
            setSelectedRide(null);
            setShowSupport(true);
          }}
        />
      )}

      {/* Support Modal */}
      {showSupport && (
        <SupportModal onClose={() => setShowSupport(false)} userProfile={userProfile} t={t} />
      )}
    </div>
  );
}
